import React, { useRef } from 'react';
import styled from 'styled-components';
import Lottie from 'react-lottie-player';
import Slider from 'react-slick';

import Text from '../basic/Text';
import Button from '../basic/Button';
import Section from '../basic/layouts/Section';
import { Grid, Cell } from '../basic/layouts/Grid';
import HTMLWrapper from '../basic/HTMLWrapper';
import ListOfLogos from '../basic/ListOfLogos';

import HeroBackground from '@images/backgrounds/hero-bg.svg';
import Wallet from '@images/hero-images/wallet.png';
import FormBuilder from '@images/hero-images/form-builder.png';
import Reporting from '@images/hero-images/reporting.png';
import Volunteer from '@images/hero-images/volunteer.png';

import theme from '../../styles/theme';
import AnimatedLine from '../basic/animated/AnimatedLine';

const {
	font,
	colors,
	breakPoints: { screenQuery, mobileFirstScreenQuery },
} = theme;

const HeroWrapper = styled.div`
	background-color: ${colors.white};
	background-image: url(${HeroBackground});
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center 120%;
	height: 100%;
	padding-top: 60px;

	${mobileFirstScreenQuery.medium} {
		padding-top: 0;
	}

	.hero__subheader {
		color: #333;
		text-align: center;
		font-size: 19px;
		line-height: 36px; /* 189.474% */
		letter-spacing: 0.19px;
		padding: 0 20px;
		margin-top: 16px;
	}

	.animated-line__wrapper {
		margin-bottom: -20px;
	}
`;

const HeroSection = styled(Section)``;

const ImageCell = styled(Cell)`
	grid-row: span 2;

	svg {
		width: 100%;
		margin-top: -70px;
	}
	${screenQuery.medium} {
		display: none;
	}
`;

const HeroContainer = styled.div`
	${mobileFirstScreenQuery.medium} {
		justify-content: center;
		align-items: center;
		justify-items: center;
		width: 100%;
		margin-left: auto;
		margin-right: auto;
		padding-top: 4rem;
		padding-bottom: 4rem;
		display: flex;
		position: relative;
		margin-top: calc(114px - 4rem);
	}
`;

const HeroHeader = styled.div`
	text-align: center;

	h1 {
		margin-bottom: 0;
		color: ${colors.black};
		font-family: ${font.recoletaMedium};
		text-align: center;
		letter-spacing: 0.56px;
		font-size: 46px;
		line-height: 52px; /* 110.714% */

		background-color: #f3ec78;
		background-image: linear-gradient(
			60deg,
			rgba(51, 51, 51, 1) 0%,
			rgba(49, 88, 123, 1) 24%,
			rgba(55, 104, 142, 1) 57%,
			rgba(46, 69, 104, 1) 82%,
			rgba(45, 68, 103, 1) 98%
		);
		color: transparent;
		background-clip: text;

		&.secondary {
			background-color: #f3ec78;
			background: linear-gradient(
				60deg,
				rgba(43, 73, 106, 1) 0%,
				rgba(42, 103, 134, 1) 24%,
				rgba(43, 83, 116, 1) 49%,
				rgba(59, 85, 127, 1) 68%,
				rgba(49, 69, 105, 1) 77%
			);
			color: transparent;
			background-clip: text;
		}

		${mobileFirstScreenQuery.medium} {
			padding: 40px 0;

			&.secondary {
				font-size: 100px;
			}
		}

		${mobileFirstScreenQuery.large} {
			font-size: 56px;
			line-height: 62px; /* 110.714% */
		}
	}

	${mobileFirstScreenQuery.medium} {
		position: absolute;
		height: 110%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		z-index: 2;
		margin-top: -5.5rem;
	}
`;

const HeroDesktopScreenshotGrid = styled(Grid)`
	display: none;
	grid-gap: 0;

	img {
		width: 100%;
	}

	${mobileFirstScreenQuery.medium} {
		display: grid;
		position: relative;

		img {
			max-width: 350px;
			margin: 0 auto;
		}
	}

	${mobileFirstScreenQuery.large} {
		img {
			max-width: 440px;
		}
	}
`;

const HeroMobileScreenshotSlide = styled.div`
	margin-top: 40px;

	img {
		width: 100%;
	}
	${mobileFirstScreenQuery.medium} {
		margin-top: none;
		display: none;
	}
`;

const CTASection = styled.div`
	display: flex;
	margin-top: 25px;
	justify-content: center;
	a {
		margin-right: 20px;
	}

	.button--secondary {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 30px;
		min-width: 150px;
		width: fit-content;
		cursor: pointer;
		border: 2px solid ${colors.brandMain};
		transition: all 0.25s ease-in 0s;
		color: ${colors.brandMain};
		background: transparent;

		:hover {
			border: 2px solid ${colors.newBrand.brandDark};
			color: ${colors.newBrand.brandDark};
		}
	}
`;

const HeroLogoLine = styled(ListOfLogos)`
	border-radius: 24px;
	background: #e8eff4;
	padding: 66px 64px;
	margin-bottom: 80px;
	margin-top: 70px;

	.section-header {
		margin-bottom: 32px;
		padding-bottom: 0;

		h2 {
			color: #6aa8b9;
			text-align: center;
			font-family: ${font.bold};
			font-size: 15px;
			line-height: normal;
			letter-spacing: 2.25px;
			text-transform: uppercase;
		}
	}
`;

export default ({
	overline,
	header,
	subheader,
	image,
	buttons,
	heroLogos,
	...props
}) => {
	const lineRef = useRef();

	const settings = {
		dots: true,
		arrows: true,
		infinite: true,
		slidesToShow: 1,
		slidesToScroll: 1,
		autoplay: false,
		cssEase: 'linear',
	};

	return (
		<>
			<HeroWrapper>
				<HeroSection maxWidth="892px" noTopPadding noBottomPadding>
					<HeroContainer>
						<HeroHeader>
							<Text component="h1">Run grant & CSR programs</Text>
							<Text component="h1" className="secondary">
								people love
							</Text>
						</HeroHeader>
						<HeroDesktopScreenshotGrid>
							<Cell span={6}>
								<img src={FormBuilder} />
							</Cell>
							<Cell span={6}>
								<img src={Volunteer} />
							</Cell>
							<Cell span={6}>
								<img src={Reporting} />
							</Cell>
							<Cell span={6}>
								<img src={Wallet} />
							</Cell>
						</HeroDesktopScreenshotGrid>
						<HeroMobileScreenshotSlide>
							<Slider {...settings}>
								<Cell span={6}>
									<img src={FormBuilder} />
								</Cell>
								<Cell span={6}>
									<img src={Volunteer} />
								</Cell>
								<Cell span={6}>
									<img src={Reporting} />
								</Cell>
								<Cell span={6}>
									<img src={Wallet} />
								</Cell>
							</Slider>
						</HeroMobileScreenshotSlide>
					</HeroContainer>
					<Text className="hero__subheader" component="b1">
						Thousands of foundations, governments, and corporations use
						Submittable to run grant, volunteering, and giving programs that
						meet people where they are and change their lives.
					</Text>
					<CTASection>
						{buttons.map((button, index) => (
							<Button secondary={index === 1} link={button.linkUrl}>
								{button.linkText}
							</Button>
						))}
					</CTASection>
					{/* <Grid>
						<Cell span={6}>
							<Text className="new-overline" component="caps" css={{ marginBottom: 30 }}>
								{overline}
							</Text>
							<Text className="header" component="h1">
								{header}
							</Text>
							<HTMLWrapper className="subtext subheader" html={subheader?.childMarkdownRemark.html} />

						</Cell>

						<ImageCell className="hero-image" span={6}>
							<Lottie animationData={HomepageAnimation} loop={false} play={true} />
						</ImageCell>
					</Grid> */}
				</HeroSection>
				<HeroLogoLine
					centerHeader
					maxWidth="984px"
					header="Powering 165,000+ programs for purpose-driven organizations"
					logos={heroLogos.logos}
					maxImgWidth={110}
					maxImgHeight={75}
				/>
				<AnimatedLine lineRef={lineRef} color={'#D3D4D8'} sectionHeight={100} />
			</HeroWrapper>
		</>
	);
};
